import {
  ownedPlaylistsNotes,
  playlistUpdatedEpochSec,
  type Playlist,
  type PlaylistPrivacy,
} from '@visious/core';
import { describeAuthProblem, type ProblemCopy } from '../../api/problem';
import type { AuthApiProblem } from '../../api/auth';
import { matchesGridSearch } from '../../listing/model';
import { fmt } from '../../strings';
import {
  ownedNoticeFor,
  ownedPlaylistsProblem,
  titleFieldState,
  toOwnedPlaylistRow,
  type OwnedPlaylistRow,
  type PlaylistsNotice,
  type TitleFieldState,
} from '../../playlists/model';

/**
 * §8's Playlists page, as reasoning — R13: no DOM, no React.
 *
 * The page behind the second gate, and the first in the app that **writes**. It lists what
 * `GET /api/v1/auth/playlists` answers and creates a new playlist; renaming, re-describing, changing the
 * visibility and deleting live on the playlist page itself, where the playlist is — so the reader is
 * looking at what they are about to change rather than at a row that represents it.
 *
 * The one thing this page holds that 2c/2d/2e deliberately do not is **form state**: a title being
 * typed. That is not a filter — it is not addressable, a `?title=` in the URL would be absurd, and Back
 * must not "undo" a half-typed word. Everything else is still derived.
 */

export interface PlaylistsFormState {
  readonly title: string;
  readonly privacy: PlaylistPrivacy;
  readonly field: TitleFieldState;
  /** `null` until a write has actually failed. */
  readonly problem: ProblemCopy | null;
  readonly busy: boolean;
  /** The `POST` cannot be sent: no valid title, or one is already in flight. */
  readonly canSubmit: boolean;
}

export function playlistsFormState(input: {
  readonly title: string;
  readonly privacy: PlaylistPrivacy;
  readonly problem: ProblemCopy | null;
  readonly busy: boolean;
}): PlaylistsFormState {
  const field = titleFieldState(input.title);
  return {
    title: input.title,
    privacy: input.privacy,
    field,
    problem: input.problem,
    busy: input.busy,
    canSubmit: field.value !== null && !input.busy,
  };
}

export type PlaylistsScreenState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'problem'; readonly copy: ProblemCopy }
  | {
      readonly kind: 'ready';
      readonly rows: readonly OwnedPlaylistRow[];
      /** The grid search emptied the page — a sentence, or an emptied grid reads as no playlists. */
      readonly nothingMatches: boolean;
      readonly notices: readonly PlaylistsNotice[];
      /**
       * The list is empty, which is a state and not a failure. Rendered *alongside* the create form
       * rather than instead of it — a page whose only content is "you have none" and no way to make one
       * is the shape §8's signed-out gate exists to avoid, one level in.
       */
      readonly empty: boolean;
      /** How many entries this one response carried. The cost, stated where it was paid. */
      readonly entriesReceived: number;
    };

export interface PlaylistsStateInput {
  readonly playlists: readonly Playlist[] | null;
  readonly droppedCount: number;
  readonly problem: AuthApiProblem | null;
  readonly nowEpochSec: number;
  /**
   * What this session set, per playlist id. The API never reports `privacy`, so this is the only source
   * of an exact value — and it is exact for precisely the playlists the reader just touched, which is the
   * case where being vague is most annoying (`describeVisibility`).
   */
  readonly knownPrivacy: ReadonlyMap<string, PlaylistPrivacy>;
  /** The grid search — display-only over the one response this collection arrives as. */
  readonly search: string;
}

export function playlistsScreenState(input: PlaylistsStateInput): PlaylistsScreenState {
  if (input.problem !== null) {
    return { kind: 'problem', copy: describeAuthProblem(input.problem, ownedPlaylistsProblem()) };
  }
  if (input.playlists === null) return { kind: 'loading' };

  const all = input.playlists.map((playlist) =>
    toOwnedPlaylistRow(
      playlist,
      input.knownPrivacy.get(playlist.playlistId) ?? null,
      // The same tolerance a listing date gets, and for the same measured reason: a playlist Invidious
      // has just refreshed reports the request's own second, once six seconds into the future.
      fmt().published(playlistUpdatedEpochSec(playlist, input.nowEpochSec), input.nowEpochSec),
    ),
  );
  const rows = all.filter((row) => matchesGridSearch(input.search, row.title));

  return {
    kind: 'ready',
    rows,
    nothingMatches: all.length > 0 && rows.length === 0,
    notices: ownedPlaylistsNotes(input.playlists, input.droppedCount).flatMap(
      (note) => ownedNoticeFor(note) ?? [],
    ),
    empty: input.playlists.length === 0,
    entriesReceived: input.playlists.reduce((sum, playlist) => sum + playlist.entries.length, 0),
  };
}
